import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import { TicketTypeSummaryResult } from 'stores/TicketTypeStoreAdapter';

/** チケットドメインのアクセント色（ヒーロー主ボタンの文字色） */
const ACCENT = '#c2185b';

type Props = {
  /** 選択可能な有効種別（作成対象） */
  activeTypes: TicketTypeSummaryResult[];
  /** 選択中の種別で作成ダイアログを開く */
  onCreate: (typeId: string) => void;
  /** ヒーローヘッダー内配置用の白基調スタイル（グラデーション上で映える） */
  hero?: boolean;
};

/**
 * チケット作成用のスプリットボタン（MUI split button 準拠）。
 * - 左の主ボタン: 現在選択中の種別で作成ダイアログを開く
 * - 右のドロップダウン: 作成する種別を選択（分類チップ付き）
 * - 有効種別が1件もない場合はボタン非活性
 */
const TicketCreateSplitButton: React.FC<Props> = ({ activeTypes, onCreate, hero = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // 有効種別の増減に追従して選択を初期化・補正
  useEffect(() => {
    if (activeTypes.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) =>
      prev && activeTypes.some((t) => t.typeId === prev) ? prev : activeTypes[0].typeId,
    );
  }, [activeTypes]);

  const selected = useMemo(
    () => activeTypes.find((t) => t.typeId === selectedId) ?? null,
    [activeTypes, selectedId],
  );

  const disabled = activeTypes.length === 0;

  // 分類ごとにグルーピング（タスク → 承認）
  const grouped = useMemo(() => {
    const task = activeTypes.filter((t) => t.ticketCategory === 'TASK');
    const approval = activeTypes.filter((t) => t.ticketCategory === 'APPROVAL');
    return { task, approval };
  }, [activeTypes]);

  const handleMainClick = () => {
    if (selected) onCreate(selected.typeId);
  };

  const handleSelect = (typeId: string) => {
    setSelectedId(typeId);
    setOpen(false);
    onCreate(typeId);
  };

  // ヒーロー配置時は白基調（半透明）でグラデーション上に映えるスタイルにする
  const heroGroupSx = hero
    ? {
        boxShadow: 'none',
        '& .MuiButtonGroup-grouped': {
          boxShadow: 'none',
          color: ACCENT,
          bgcolor: '#fff',
          borderColor: 'rgba(255,255,255,0.85)',
          fontWeight: 700,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.88)', borderColor: '#fff' },
          '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.7)',
            bgcolor: 'rgba(255,255,255,0.18)',
            borderColor: 'rgba(255,255,255,0.2)',
          },
        },
        '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderRightColor: alpha(ACCENT, 0.25) },
      }
    : { boxShadow: 'none', '& .MuiButtonGroup-grouped': { boxShadow: 'none' } };

  return (
    <>
      <ButtonGroup ref={anchorRef} variant="contained" disabled={disabled} sx={heroGroupSx}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleMainClick}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}
        >
          {disabled
            ? '作成できる種別がありません'
            : selected
              ? `作成: ${selected.name}`
              : 'チケット作成'}
        </Button>
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          sx={{ px: 0.5, borderRadius: 2, boxShadow: 'none' }}
          aria-label="種別を選択"
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
            <Paper
              elevation={0}
              sx={{
                mt: 0.5,
                minWidth: 260,
                maxHeight: 360,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList dense sx={{ py: 0.5 }}>
                  <ListSubheader
                    sx={{ lineHeight: '32px', bgcolor: 'transparent', fontWeight: 700 }}
                  >
                    作成する種別を選択
                  </ListSubheader>
                  {grouped.task.length > 0 && (
                    <TypeGroup
                      label="タスク"
                      types={grouped.task}
                      selectedId={selectedId}
                      onSelect={handleSelect}
                    />
                  )}
                  {grouped.approval.length > 0 && (
                    <TypeGroup
                      label="承認"
                      types={grouped.approval}
                      selectedId={selectedId}
                      onSelect={handleSelect}
                    />
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

const TypeGroup: React.FC<{
  label: string;
  types: TicketTypeSummaryResult[];
  selectedId: string | null;
  onSelect: (typeId: string) => void;
}> = ({ label, types, selectedId, onSelect }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ px: 2, pt: 0.5, display: 'block' }}
    >
      {label}
    </Typography>
    {types.map((t) => (
      <MenuItem
        key={t.typeId}
        selected={t.typeId === selectedId}
        onClick={() => onSelect(t.typeId)}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Box sx={{ width: 16, flexShrink: 0, display: 'flex' }}>
            {t.typeId === selectedId && <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
          </Box>
          <TicketCategoryChip ticketCategory={t.ticketCategory} />
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {t.name}
          </Typography>
        </Stack>
      </MenuItem>
    ))}
  </Box>
);

export default TicketCreateSplitButton;
